<template>
  <div
    class="gallery-picker"
    :class="{ 'gallery-picker--picker': mode === 'picker' }"
  >
    <div class="gallery-sidebar">
      <div
        class="sidebar-item"
        :class="{ active: !activeDirectoryId }"
        @click="handleSelectDirectory(null)"
      >
        <i class="el-icon-folder" />
        <span>{{ $t('sc.gallery.allMaterials') }}</span>
      </div>
      <el-tree
        v-if="directoryTree.length"
        ref="directoryTreeRef"
        :data="directoryTree"
        :props="treeProps"
        node-key="id"
        :highlight-current="true"
        :expand-on-click-node="true"
        :default-expand-all="true"
        @node-click="handleTreeNodeClick"
      >
        <template #default="{ node, data }">
          <span
            class="tree-node-label"
            @contextmenu.prevent="handleTreeContextMenu($event, data)"
          >
            <i class="el-icon-folder" />
            <span :title="node.label" class="tree-node-text">{{ node.label }}</span>
          </span>
        </template>
      </el-tree>

      <div
        v-show="contextMenu.visible"
        ref="contextMenuRef"
        class="tree-context-menu"
        :style="{ top: contextMenu.top + 'px', left: contextMenu.left + 'px' }"
      >
        <div
          class="context-menu-item"
          @click="handleRenameFolder"
        >
          <i class="el-icon-edit" />
          <span>{{ $t('sc.gallery.renameFolder') }}</span>
        </div>
        <!-- <div
          class="context-menu-item context-menu-item--danger"
          @click="handleDeleteFolder"
        >
          <i class="el-icon-delete" />
          <span>{{ $t('sc.gallery.deleteFolder') }}</span>
        </div> -->
      </div>
      <div
        class="sidebar-add-folder"
        @click="showNewFolderDialog = true"
      >
        {{ $t('sc.gallery.addFolder') }}
      </div>
    </div>

    <div class="gallery-content">
      <div class="gallery-toolbar">
        <div v-if="defaultFileType === 'all'" class="toolbar-tabs">
          <span
            class="tab-item"
            :class="{ active: activeTab === 'all' }"
            @click="handleTabChange('all')"
          >
            {{ $t('sc.gallery.allMaterials') }}
          </span>
          <span
            class="tab-item"
            :class="{ active: activeTab === 'image' }"
            @click="handleTabChange('image')"
          >
            {{ $t('sc.gallery.images') }}
          </span>
          <span
            class="tab-item"
            :class="{ active: activeTab === 'video' }"
            @click="handleTabChange('video')"
          >
            {{ $t('sc.gallery.videos') }}
          </span>
        </div>
        <div class="toolbar-actions">
          <el-checkbox
            v-if="mode === 'page'"
            v-model="isAllSelected"
            class="select-all-checkbox"
            @change="handleSelectAll"
          >
            {{ $t('sc.gallery.selectAll') }}
          </el-checkbox>
          <el-button
            size="small"
            @click="showNewFolderDialog = true"
          >
            {{ $t('sc.gallery.newFolder') }}
          </el-button>
          <el-button
            size="small"
            type="danger"
            @click="showUploadDialog = true"
          >
            {{ $t('sc.gallery.localUpload') }}
          </el-button>
        </div>
      </div>

      <div
        v-loading="loading || loadingMore"
        class="gallery-grid-wrap"
      >
        <div
          ref="galleryGrid"
          class="gallery-grid"
          @scroll="throttledScroll"
        >
          <div
            v-for="file in fileList"
            :key="file.id"
            class="gallery-item"
            :class="{
              selected: selectedFileIds.includes(file.id),
              referenced: file.isReferenced
            }"
            @click="handleFileClick(file)"
          >
            <div class="item-thumbnail">
              <template v-if="isVideoFile(file)">
                <video
                  class="video-player"
                  width="100%"
                  controls="controls"
                >
                  <source :src="file.fullUrl" type="video/mp4">
                </video>
              </template>
              <template v-else>
                <img
                  :src="file.fullUrl || file.thumbnailUrl"
                  :alt="file.fileName"
                >
              </template>
              <div
                v-if="file.isReferenced"
                class="referenced-badge"
              >
                {{ $t('sc.gallery.referenced') }}
              </div>
              <div
                v-if="selectedFileIds.includes(file.id)"
                class="item-check-icon"
              >
                <span class="order-num">{{ getSelectedOrder(file.id) }}</span>
              </div>
            </div>
            <div class="item-info">
              <div class="item-name" :title="file.fileName">
                {{ file.fileName }}
              </div>
              <div class="item-meta">
                <span v-if="formatFileSize(file.fileSize)">{{ formatFileSize(file.fileSize) }}</span>
                <span>{{ formatDate(file.createdTime) }}</span>
              </div>
              <div class="item-hover-actions">
                <span
                  v-if="!file.isReferenced"
                  class="hover-action"
                  @click.stop="handleSingleFileDelete(file)"
                >
                  <i class="el-icon-delete" />
                  {{ $t('sc.gallery.delete') }}
                </span>
                <span
                  class="hover-action"
                  @click.stop="handlePreviewFile(file)"
                >
                  <i class="el-icon-view" />
                  {{ $t('sc.gallery.preview') }}
                </span>
              </div>
            </div>
          </div>
          <div
            v-if="!loading && fileList.length === 0"
            class="gallery-empty"
          >
            <no-data />
          </div>
          <div
            v-if="fileList.length > 0 && (loadingMore || !hasMore)"
            class="grid-loadmore-status"
          >
            <span v-if="loadingMore">
              <i class="el-icon-loading" />
              {{ $t('sc.gallery.loadingMore') }}
            </span>
            <span v-else>{{ $t('sc.gallery.noMore', { n: pagination.total }) }}</span>
          </div>
        </div>
      </div>

      <div class="gallery-footer" :class="{ 'is-picker': mode === 'picker' }">
        <div class="footer-left">
          <el-button
            v-if="mode === 'page' && selectedFileIds.length > 0"
            type="danger"
            size="small"
            @click="handleDeleteFiles"
          >
            {{ $t('sc.gallery.delete') }}
          </el-button>
          <span
            v-if="mode === 'picker' && maxSelect > 0"
            class="picker-counter"
          >
            {{ $t('sc.gallery.selectedCount', { n: selectedFileIds.length, max: maxSelect }) }}
          </span>
        </div>
        <div class="footer-right">
          <span class="progress-text">
            {{ $t('sc.gallery.shownProgress', { shown: fileList.length, total: pagination.total }) }}
          </span>
          <template v-if="mode === 'picker'">
            <el-button
              size="small"
              @click="handleClose"
            >
              {{ $t('sc.gallery.closeGallery') }}
            </el-button>
            <el-button
              type="danger"
              size="small"
              :disabled="selectedFileIds.length === 0"
              @click="handleConfirmSelect"
            >
              {{ useSelectedLabel }}
            </el-button>
          </template>
        </div>
      </div>
    </div>

    <NewFolderDialog
      :visible.sync="showNewFolderDialog"
      :directory-tree="directoryTree"
      @created="handleFolderCreated"
    />

    <UploadDialog
      :visible.sync="showUploadDialog"
      :directory-tree="directoryTree"
      @uploaded="handleFileUploaded"
    />

    <el-dialog
      :title="$t('sc.gallery.renameFolderTitle')"
      :visible.sync="renameDialog.visible"
      width="420px"
      append-to-body
      :close-on-click-modal="false"
    >
      <el-form label-position="top">
        <el-form-item :label="$t('sc.gallery.newFolderNameLabel')">
          <el-input
            v-model="renameDialog.newName"
            :placeholder="$t('sc.gallery.newFolderNamePlaceholder')"
          />
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button
          type="primary"
          :loading="renameDialog.submitting"
          @click="submitRenameFolder"
        >
          {{ $t('sc.gallery.confirm') }}
        </el-button>
        <el-button @click="renameDialog.visible = false">
          {{ $t('sc.gallery.cancel') }}
        </el-button>
      </template>
    </el-dialog>

    <el-dialog
      :visible.sync="previewDialog.visible"
      :title="previewDialog.fileName"
      width="720px"
      append-to-body
      class="preview-dialog"
    >
      <div class="preview-content">
        <video
          v-if="previewDialog.isVideo"
          :src="previewDialog.url"
          controls="controls"
          class="preview-media-video"
        />
        <img
          v-else
          :src="previewDialog.url"
          :alt="previewDialog.fileName"
          class="preview-media"
        >
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { throttle } from 'lodash'
import {
  getGalleryDirectoryTree,
  getGalleryFiles,
  deleteGalleryFile,
  batchDeleteGalleryFiles,
  deleteGalleryDirectory,
  updateGalleryDirectory
} from '_h/gallery'
import NewFolderDialog from './NewFolderDialog.vue'
import UploadDialog from './UploadDialog.vue'

// 触底加载阈值（距离底部 px）
const SCROLL_LOAD_MORE_THRESHOLD = 100
// 滚动事件节流间隔
const SCROLL_THROTTLE_MS = 200

export default {
  name: 'GalleryPicker',
  components: {
    NewFolderDialog,
    UploadDialog
  },
  props: {
    // 'page' = 素材空间完整页面模式, 'picker' = 选择器弹窗模式
    mode: {
      type: String,
      default: 'picker',
      validator: v => ['page', 'picker'].includes(v)
    },
    // 预设文件类型筛选
    defaultFileType: {
      type: String,
      default: 'all',
      validator: v => ['all', 'image', 'video'].includes(v)
    },
    // picker 模式下是否允许多选
    multiple: {
      type: Boolean,
      default: true
    },
    // picker 模式下最大选择数量, 0 表示不限制
    maxSelect: {
      type: Number,
      default: 0
    }
  },
  data() {
    return {
      directoryTree: [],
      treeProps: {
        label: 'name',
        children: 'children'
      },
      activeDirectoryId: null,
      activeTab: this.defaultFileType,
      fileList: [],
      selectedFileIds: [],
      selectedFiles: [],
      isAllSelected: false,
      loading: false,
      loadingMore: false,
      hasMore: true,
      pagination: {
        pageNo: 1,
        pageSize: 20,
        total: 0
      },
      showNewFolderDialog: false,
      showUploadDialog: false,
      contextMenu: {
        visible: false,
        top: 0,
        left: 0,
        currentNode: null
      },
      renameDialog: {
        visible: false,
        newName: '',
        submitting: false
      },
      previewDialog: {
        visible: false,
        url: '',
        fileName: '',
        isVideo: false
      }
    }
  },
  computed: {
    useSelectedLabel() {
      const baseKey = this.activeTab === 'video'
        ? 'sc.gallery.useSelectedVideos'
        : 'sc.gallery.useSelectedImages'
      const base = this.$t(baseKey)
      return this.selectedFileIds.length > 0
        ? `${base} (${this.selectedFileIds.length})`
        : base
    }
  },
  watch: {
    selectedFileIds(val) {
      if (this.mode === 'page' && this.fileList.length > 0) {
        const selectableFiles = this.fileList.filter(f => this.isFileSelectable(f))
        this.isAllSelected = selectableFiles.length > 0 && selectableFiles.every(f => val.includes(f.id))
      }
    }
  },
  created() {
    // 节流后的滚动处理：实例独占，避免多实例共享节流计数
    this.throttledScroll = throttle(this.handleScrollCheck, SCROLL_THROTTLE_MS)
  },
  mounted() {
    this.fetchDirectoryTree()
    this.fetchFiles()
    document.addEventListener('click', this.closeContextMenu)
  },
  beforeDestroy() {
    document.removeEventListener('click', this.closeContextMenu)
    if (this.throttledScroll && typeof this.throttledScroll.cancel === 'function') {
      this.throttledScroll.cancel()
    }
  },
  methods: {
    async fetchDirectoryTree() {
      try {
        const data = await getGalleryDirectoryTree(this.$axios)
        this.directoryTree = data || []
      } catch (error) {
        console.error('fetchDirectoryTree error:', error)
      }
    },

    async fetchFiles(append = false) {
      if (append) {
        if (!this.hasMore || this.loadingMore || this.loading) return
        this.loadingMore = true
      } else {
        this.loading = true
        this.pagination.pageNo = 1
        this.hasMore = true
        if (this.$refs.galleryGrid) this.$refs.galleryGrid.scrollTop = 0
      }
      try {
        const params = {
          pageNo: String(this.pagination.pageNo),
          pageSize: String(this.pagination.pageSize)
        }
        if (this.activeDirectoryId) {
          params.directoryId = String(this.activeDirectoryId)
        } else {
          params.isReturnAll = 'true'
        }
        if (this.activeTab !== 'all') {
          params.fileType = this.activeTab
        }
        const data = await getGalleryFiles(this.$axios, params)
        const items = data?.items || []
        this.fileList = append ? [...this.fileList, ...items] : items
        this.pagination.total = data?.total || 0
        this.hasMore = this.fileList.length < this.pagination.total && items.length > 0
      } catch (error) {
        console.error('fetchFiles error:', error)
        if (!append) this.fileList = []
        this.hasMore = false
      } finally {
        this.loading = false
        this.loadingMore = false
      }
    },

    loadMore() {
      if (this.loading || this.loadingMore || !this.hasMore) return
      this.pagination.pageNo += 1
      this.fetchFiles(true)
    },

    handleScrollCheck(e) {
      const el = e && e.target
      if (!el) return
      if (this.loading || this.loadingMore || !this.hasMore) return
      const distanceToBottom = el.scrollHeight - el.scrollTop - el.clientHeight
      if (distanceToBottom < SCROLL_LOAD_MORE_THRESHOLD) this.loadMore()
    },

    handleSelectDirectory(directoryId) {
      this.activeDirectoryId = directoryId
      if (this.$refs.directoryTreeRef) {
        this.$refs.directoryTreeRef.setCurrentKey(null)
      }
      this.pagination.pageNo = 1
      this.selectedFileIds = []
      this.selectedFiles = []
      this.fetchFiles()
    },

    handleTreeNodeClick(data) {
      this.activeDirectoryId = data.id
      this.pagination.pageNo = 1
      this.selectedFileIds = []
      this.selectedFiles = []
      this.fetchFiles()
    },

    handleTabChange(tab) {
      this.activeTab = tab
      this.pagination.pageNo = 1
      this.selectedFileIds = []
      this.selectedFiles = []
      this.fetchFiles()
    },

    isVideoFile(file) {
      if (file.fileType === 'video') return true
      const videoExts = ['.mp4', '.avi', '.mov', '.wmv', '.flv', '.webm']
      return videoExts.some(ext => file.fileName?.toLowerCase().endsWith(ext))
    },

    isFileSelectable(file) {
      if (this.mode === 'page') {
        return !file.isReferenced
      }
      return true
    },

    // 文件在 selectedFileIds 中的顺序（1-based），未选中返回 null
    getSelectedOrder(fileId) {
      const index = this.selectedFileIds.indexOf(fileId)
      return index === -1 ? null : index + 1
    },

    handleFileClick(file) {
      if (this.mode === 'page' && file.isReferenced) {
        this.$message.warning(this.$t('sc.gallery.referencedItemNotSelectable'))
        return
      }
      this.handleFileSelect(file)
    },

    handleFileSelect(file) {
      if (this.mode === 'page' && file.isReferenced) return

      const index = this.selectedFileIds.indexOf(file.id)
      if (index > -1) {
        this.selectedFileIds.splice(index, 1)
        this.selectedFiles = this.selectedFiles.filter(f => f.id !== file.id)
      } else {
        if (!this.multiple) {
          this.selectedFileIds = [file.id]
          this.selectedFiles = [file]
        } else if (this.maxSelect > 0 && this.selectedFileIds.length >= this.maxSelect) {
          this.$message.warning(this.$t('sc.gallery.selectionLimitReached', { max: this.maxSelect }))
        } else {
          this.selectedFileIds.push(file.id)
          this.selectedFiles.push(file)
        }
      }
    },

    handleSelectAll(checked) {
      if (checked) {
        const selectableFiles = this.fileList.filter(f => this.isFileSelectable(f))
        this.selectedFileIds = selectableFiles.map(f => f.id)
        this.selectedFiles = [...selectableFiles]
      } else {
        this.selectedFileIds = []
        this.selectedFiles = []
      }
    },

    async handleDeleteFiles() {
      if (this.selectedFileIds.length === 0) {
        this.$message.warning(this.$t('sc.gallery.noFileSelected'))
        return
      }

      try {
        await this.$confirm(
          this.$t('sc.gallery.deleteConfirm'),
          { type: 'warning' }
        )
      } catch {
        return
      }

      try {
        await batchDeleteGalleryFiles(this.$axios, this.selectedFileIds)
        this.$message.success(this.$t('sc.gallery.deleteSuccess'))
        this.selectedFileIds = []
        this.selectedFiles = []
        this.fetchFiles()
        this.$emit('filesChanged')
      } catch (error) {
        console.error('deleteFiles error:', error)
        this.$message.error(this.$t('sc.gallery.deleteFailed'))
      }
    },

    handleTreeContextMenu(event, data) {
      this.contextMenu.currentNode = data
      this.contextMenu.top = event.clientY
      this.contextMenu.left = event.clientX
      this.contextMenu.visible = true
    },

    closeContextMenu() {
      this.contextMenu.visible = false
    },

    handleRenameFolder() {
      const node = this.contextMenu.currentNode
      this.contextMenu.visible = false
      if (!node) return
      this.renameDialog.newName = node.name
      this.renameDialog.visible = true
    },

    async submitRenameFolder() {
      const name = this.renameDialog.newName?.trim()
      if (!name) {
        this.$message.warning(this.$t('sc.gallery.folderNameRequired'))
        return
      }
      this.renameDialog.submitting = true
      try {
        await updateGalleryDirectory(
          this.$axios,
          this.contextMenu.currentNode.id,
          { name }
        )
        this.contextMenu.currentNode.name = name
        this.$message.success(this.$t('sc.gallery.renameFolderSuccess'))
        this.renameDialog.visible = false
        this.fetchDirectoryTree()
      } catch (error) {
        console.error('renameFolder error:', error)
        this.$message.error(this.$t('sc.gallery.renameFolderFailed'))
      } finally {
        this.renameDialog.submitting = false
      }
    },

    async handleDeleteFolder() {
      const node = this.contextMenu.currentNode
      this.contextMenu.visible = false
      if (!node) return

      try {
        await this.$confirm(
          this.$t('sc.gallery.deleteFolderConfirm'),
          { type: 'warning' }
        )
      } catch {
        return
      }

      try {
        await deleteGalleryDirectory(this.$axios, node.id)
        this.$message.success(this.$t('sc.gallery.deleteFolderSuccess'))
        if (this.activeDirectoryId === node.id) {
          this.activeDirectoryId = null
        }
        this.fetchDirectoryTree()
        this.fetchFiles()
      } catch (error) {
        console.error('deleteFolder error:', error)
        this.$message.error(this.$t('sc.gallery.deleteFolderFailed'))
      }
    },

    async handleSingleFileDelete(file) {
      try {
        await this.$confirm(
          this.$t('sc.gallery.deleteFileConfirm'),
          { type: 'warning' }
        )
      } catch {
        return
      }

      try {
        await deleteGalleryFile(this.$axios, file.id)
        this.$message.success(this.$t('sc.gallery.deleteFileSuccess'))
        this.selectedFileIds = this.selectedFileIds.filter(id => id !== file.id)
        this.selectedFiles = this.selectedFiles.filter(f => f.id !== file.id)
        this.fetchFiles()
        this.$emit('filesChanged')
      } catch (error) {
        console.error('deleteSingleFile error:', error)
        this.$message.error(this.$t('sc.gallery.deleteFileFailed'))
      }
    },

    handlePreviewFile(file) {
      this.previewDialog.url = file.fullUrl || file.fileUrl || file.thumbnailUrl
      this.previewDialog.fileName = file.fileName
      this.previewDialog.isVideo = this.isVideoFile(file)
      this.previewDialog.visible = true
    },

    handleFolderCreated() {
      this.fetchDirectoryTree()
    },

    handleFileUploaded() {
      this.fetchFiles()
    },

    handleClose() {
      this.$emit('close')
    },

    handleConfirmSelect() {
      if (this.selectedFiles.length === 0) {
        this.$message.warning(this.$t('sc.gallery.noFileSelected'))
        return
      }
      // 二次校验，防止历史选中或边界场景绕过 handleFileSelect 闸门
      if (this.maxSelect > 0 && this.selectedFiles.length > this.maxSelect) {
        this.$message.warning(this.$t('sc.gallery.selectionLimitReached', { max: this.maxSelect }))
        return
      }
      this.$emit('select', this.selectedFiles)
    },

    formatFileSize(size) {
      if (!size) return ''
      if (size < 1024) return `${size}B`
      if (size < 1024 * 1024) return `${(size / 1024).toFixed(0)}kb`
      return `${(size / 1024 / 1024).toFixed(1)}MB`
    },

    formatDate(date) {
      if (!date) return ''
      const d = new Date(date)
      const year = d.getFullYear()
      const month = String(d.getMonth() + 1).padStart(2, '0')
      const day = String(d.getDate()).padStart(2, '0')
      return `${year}-${month}-${day}`
    }
  }
}
</script>

<style lang="scss" scoped>
.gallery-picker {
  display: flex;
  height: 100%;
  background: #fff;
  border-radius: 4px;
  border: 1px solid #e8e8e8;

  // picker 模式被嵌在 el-dialog 中，body 默认无固定高度，
  // 显式约束让 .gallery-grid 的 overflow-y:auto 生效，触底滚动加载才能触发。
  &--picker {
    height: 70vh;
    min-height: 480px;
    max-height: 720px;
  }
}

.gallery-sidebar {
  width: 300px;
  min-width: 200px;
  max-width: 300px;
  border-right: 1px solid #e8e8e8;
  padding: 0;
  display: flex;
  flex-direction: column;

  .sidebar-item {
    padding: 8px 16px;
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 14px;
    color: #333;

    &:hover {
      background: #f5f5f5;
    }

    &.active {
      color: #dd3131;
      font-weight: bold;
    }

    i {
      font-size: 18px;
    }
  }

  /deep/ .el-tree {
    flex: 1;
    overflow-y: auto;

    .el-tree-node__content {
      height: 36px;
    }

    .el-tree-node.is-current > .el-tree-node__content {
      color: #dd3131;
      background-color: #fff1f0;
    }

    .tree-node-label {
      display: flex;
      align-items: center;
      gap: 6px;
      font-size: 14px;
      overflow: hidden;
      width: 100%;

      i {
        flex-shrink: 0;
        font-size: 16px;
      }

      .tree-node-text {
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }
    }
  }

  .sidebar-add-folder {
    padding: 0 16px;
    cursor: pointer;
    color: #666;
    font-size: 13px;
    border-top: 1px solid #e8e8e8;
    margin-top: auto;
    height: 55px;
    line-height: 55px;
    &:hover {
      color: #dd3131;
    }
  }

  .tree-context-menu {
    position: fixed;
    z-index: 9999;
    background: #fff;
    border: 1px solid #e4e7ed;
    border-radius: 4px;
    box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
    padding: 4px 0;
    min-width: 120px;

    .context-menu-item {
      display: flex;
      align-items: center;
      gap: 8px;
      padding: 8px 16px;
      font-size: 13px;
      color: #333;
      cursor: pointer;
      white-space: nowrap;

      &:hover {
        background: #f5f7fa;
      }

      &--danger {
        color: #f56c6c;

        &:hover {
          background: #fef0f0;
        }
      }

      i {
        font-size: 14px;
      }
    }
  }
}

.gallery-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.gallery-toolbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  border-bottom: 1px solid #e8e8e8;

  .toolbar-tabs {
    display: flex;
    gap: 24px;

    .tab-item {
      font-size: 14px;
      color: #666;
      cursor: pointer;
      padding-bottom: 4px;
      font-weight: bold;

      &:hover {
        color: #dd3131;
      }

      &.active {
        color: #dd3131;
        border-bottom: 2px solid #dd3131;
      }
    }
  }

  .toolbar-actions {
    display: flex;
    align-items: center;
    gap: 12px;

    .select-all-checkbox {
      margin-right: 8px;
    }

    /deep/ .el-button--danger {
      background-color: #dd3131;
      border-color: #dd3131;

      &:hover {
        background-color: #c42b2b;
        border-color: #c42b2b;
      }
    }
  }
}

.gallery-grid-wrap {
  flex: 1;
  display: flex;
  min-height: 0;
  position: relative;
}

.gallery-grid {
  flex: 1;
  overflow-y: auto;
  padding: 16px;
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  align-content: flex-start;
}

.gallery-item {
  width: calc((100% - 72px) / 5);
  cursor: pointer;
  border-radius: 4px;
  transition: all 0.2s;

  &:hover {
    .item-thumbnail {
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
    }
  }

  &.selected {
    .item-thumbnail {
      border: 2px solid #dd3131;
    }
  }

  &.referenced {
    opacity: 0.85;
  }

  .item-thumbnail {
    position: relative;
    width: 100%;
    padding-top: 100%;
    border: 2px solid transparent;
    border-radius: 4px;
    overflow: hidden;
    background: #f5f5f5;

    img {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      object-fit: cover;
    }

    .video-player {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      object-fit: cover;
      background: #1a1a2e;
    }

    .referenced-badge {
      position: absolute;
      top: 4px;
      right: 4px;
      background: #ff9800;
      color: #fff;
      font-size: 11px;
      padding: 2px 6px;
      border-radius: 2px;
      white-space: nowrap;
    }

    .item-check-icon {
      position: absolute;
      top: 6px;
      right: 6px;
      width: 24px;
      height: 24px;
      border-radius: 50%;
      background: #dd3131;
      display: flex;
      align-items: center;
      justify-content: center;
      z-index: 1;

      i {
        font-size: 14px;
        color: #fff;
        font-weight: bold;
      }

      .order-num {
        font-size: 12px;
        color: #fff;
        font-weight: bold;
        line-height: 1;
      }
    }
  }

  .item-info {
    padding: 6px 2px;
    position: relative;

    .item-name {
      font-size: 13px;
      color: #333;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    .item-meta {
      display: flex;
      gap: 12px;
      font-size: 12px;
      color: #999;
      margin-top: 2px;
    }

  }
}

.gallery-item .item-hover-actions {
  position: absolute;
  left: 0;
  bottom: 0;
  width: 100%;
  display: none;
  align-items: center;
  gap: 12px;
  padding: 4px 2px;
  background: rgba(255, 255, 255, 0.95);

  .hover-action {
    display: inline-flex;
    align-items: center;
    gap: 3px;
    font-size: 12px;
    color: #666;
    cursor: pointer;
    white-space: nowrap;

    i {
      font-size: 14px;
    }

    &:hover {
      color: #dd3131;
    }
  }
}

.gallery-item:hover .item-hover-actions {
  display: flex;
}

.gallery-empty {
  grid-column: 1 / -1;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 200px;
}

.grid-loadmore-status {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 12px 0;
  font-size: 13px;
  color: #999;

  i {
    margin-right: 6px;
    font-size: 14px;
  }
}

.gallery-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0 16px;
  height: 55px;
  line-height: 55px;
  border-top: 1px solid #e8e8e8;
  background: #fff;
  box-shadow: 0 -2px 8px rgba(0, 0, 0, 0.04);
  flex-shrink: 0;

  .footer-left {
    display: flex;
    align-items: center;
    gap: 12px;

    .picker-counter {
      font-size: 13px;
      color: #666;
    }
  }

  .footer-right {
    display: flex;
    align-items: center;
    gap: 16px;

    .progress-text {
      font-size: 13px;
      color: #999;
    }
  }

  /deep/ .el-button--danger {
    background-color: #dd3131;
    border-color: #dd3131;

    &:hover {
      background-color: #c42b2b;
      border-color: #c42b2b;
    }
  }
}

.preview-dialog {
  .el-dialog__body {
    padding: 0 20px 20px;
    text-align: center;
  }

  .preview-content {
    display: flex;
    align-items: center;
    justify-content: center;

    .preview-media {
      width: 100%;
      max-width: 100%;
      max-height: 60vh;
      object-fit: contain;
      border-radius: 4px;
    }
    .preview-media-video {
      width: 100%;
      max-width: 100%;
      max-height: 680px;
      object-fit: contain;
      border-radius: 4px;
    }
  }
}

</style>
